import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Alert,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Button,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ShareIcon from '@mui/icons-material/Share';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import { useParams } from 'react-router-dom';

import { useGetUnitByIdQuery } from '../units/unitsApi';
import { useGetCourseByIdQuery } from '../my-courses/coursesApi';
import ShareDialog from '../../shared/components/ShareDialog';

import { useGetLessonByIdQuery, useUpdateLessonMutation } from './lessonsApi';
import { LessonContent } from './components/LessonContent';
import { LessonOverviewCard } from './components/LessonOverviewCard';

import type { AgentContext } from '@/shared/types/chat';
import type { StoredStandard } from '@/shared/types/customStandard';
import type {
  CreateResourceRequest,
  GenerateFromTemplateRequest,
  Resource,
  ResourceParentLocator,
} from '@/shared/types/resource';
import {
  stripStorageImagePathsFromDocumentContent,
  stripStorageImagePathsFromSlideContent,
} from '@/shared/types/resource';
import { PageSpinner } from '@/shared/components/PageSpinner';
import AccessDeniedOrNotFound from '@/shared/components/AccessDeniedOrNotFound';
import { EditableText } from '@/shared/components/EditableText';
import { useAppSelector, useAppDispatch } from '@/shared/hooks/redux';
import { useObjectPermissionState } from '@/shared/hooks/useCanEdit';
import { setIsMobile, setScopedAiContext, toggleScopedAiPanel } from '@/shared/store/uiSlice';
import ResourceListCard from '@/shared/components/ResourceListCard';
import { exportCurriculumPdf } from '@/shared/services/pdfExport';
import { useSetPublicCascadeMutation } from '@/features/permissions/permissionsApi';
import {
  useCreateFromTemplateMutation,
  useCreateResourceMutation,
  useDeleteResourceMutation,
  useGetResourcesQuery,
  useGetResourceTemplatesQuery,
  useUpdateResourceMutation,
  useGenerateFromTemplateMutation,
} from '@/features/resources/resourcesApi';
import { useGoogleDocsExport } from '@/features/resources/hooks/useGoogleDocsExport';
import { isFeatureEnabled } from '@/shared/libs/featureFlags';
import { ScoperAiDialog } from '@/features/chatbot/scoper-dev';

const LessonPage: React.FC = () => {
  const pdfExportEnabled = isFeatureEnabled('curriculumPdfExport');
  const scoperEnabled = import.meta.env.DEV && isFeatureEnabled('scoperAiDevDialog');
  const [scoperOpen, setScoperOpen] = useState(false);
  const { lessonId } = useParams<{ lessonId: string }>();
  const { data: lesson, error, isLoading } = useGetLessonByIdQuery(lessonId!);
  // Call parent queries unconditionally with skip flags to preserve hook order
  const unitIdForScope = lesson?.unit_id;
  const { data: unitForScope } = useGetUnitByIdQuery(unitIdForScope as string, {
    skip: !unitIdForScope,
  });
  const courseIdForScope = unitForScope?.course_id;
  const scoperContext = useMemo<AgentContext | null>(
    () =>
      courseIdForScope && lesson
        ? {
            assistantType: 'course',
            courseId: courseIdForScope,
            focus: {
              level: 'lesson',
              courseId: courseIdForScope,
              unitId: unitIdForScope,
              lessonId: lesson.id,
            },
          }
        : null,
    [courseIdForScope, lesson, unitIdForScope]
  );
  const { data: courseForScope } = useGetCourseByIdQuery(courseIdForScope as string, {
    skip: !courseIdForScope,
  });

  const [updateLesson, { isLoading: isUpdatingLesson }] = useUpdateLessonMutation();
  const [setPublicCascade] = useSetPublicCascadeMutation();
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const { user } = useAppSelector((state) => state.auth);
  const isOwner = Boolean(lesson && user && lesson.owner_id === user.id);
  const lessonPermission = useObjectPermissionState('lesson', lessonId, 'editor', {
    fallbackAllowed: isOwner,
  });
  const canEdit = lessonPermission.isAllowed;
  const lessonListLocator: ResourceParentLocator = {
    parentLessonId: lessonId!,
    courseId: courseIdForScope ?? undefined,
  };
  const lessonWriteLocator: ResourceParentLocator = {
    parentLessonId: lessonId!,
    courseId: courseIdForScope ?? undefined,
  };
  const canManageResources = canEdit;
  const { data: resources = [] } = useGetResourcesQuery(lessonListLocator, { skip: !lessonId });
  const { data: templates = [] } = useGetResourceTemplatesQuery({ templateKind: 'document' });
  const [createResource] = useCreateResourceMutation();
  const [updateResource] = useUpdateResourceMutation();
  const [deleteResource] = useDeleteResourceMutation();
  const [createFromTemplate] = useCreateFromTemplateMutation();
  const [generateFromTemplate] = useGenerateFromTemplateMutation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useAppDispatch();
  const { exportToGoogle } = useGoogleDocsExport({ onError: setExportError });

  useEffect(() => {
    dispatch(setIsMobile(isMobile));
  }, [isMobile, dispatch]);

  useEffect(() => {
    if (!lesson) return;
    dispatch(
      setScopedAiContext({
        level: 'lesson',
        courseId: courseIdForScope,
        unitId: unitIdForScope,
        lessonId: lesson.id,
        resourceId: undefined,
        resourceKind: undefined,
      })
    );
  }, [dispatch, lesson, courseIdForScope, unitIdForScope]);

  // Collect stored standards from unit and course overview items as the lesson's parentScope
  const parentScope = React.useMemo(() => {
    const map = new Map<string, StoredStandard>();
    const collect = (entity: any) =>
      (entity?.content?.overview ?? [])
        .filter((it: any) => it?.type === 'standards')
        .flatMap((it: any) => it?.standards ?? [])
        .forEach((s: StoredStandard) => map.set(s.id, s));
    collect(unitForScope);
    collect(courseForScope);
    return Array.from(map.values());
  }, [unitForScope, courseForScope]);

  if (isLoading) {
    return <PageSpinner />;
  }
  if (error || !lesson) {
    return <AccessDeniedOrNotFound objectType="lesson" objectId={lessonId} user={user} />;
  }

  // Handle title update
  const handleUpdateTitle = async (newTitle: string) => {
    await updateLesson({ id: lesson.id, title: newTitle }).unwrap();
  };

  // Handle description update
  const handleUpdateDescription = async (newDescription: string) => {
    await updateLesson({ id: lesson.id, description: newDescription }).unwrap();
  };

  const handleExportPdf = async () => {
    if (!lesson) return;
    setExportError(null);
    setIsExportingPdf(true);
    try {
      await exportCurriculumPdf(
        'lesson',
        lesson.id,
        `${lesson.title || 'lesson'}-lesson-export.pdf`
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to export PDF';
      setExportError(message);
    } finally {
      setIsExportingPdf(false);
    }
  };

  const handleCreateResource = async (resourceData: CreateResourceRequest) => {
    if (!lesson) return;
    return createResource({
      ...lessonWriteLocator,
      resource: resourceData,
    }).unwrap();
  };

  const handleUpdateResource = async (resource: Resource) => {
    await updateResource({
      id: resource.id,
      title: resource.title,
      description: resource.description,
      content: resource.content,
      version: (resource.version ?? 1) + 1,
    }).unwrap();
  };

  const handleDeleteResource = async (resourceId: string) => {
    if (!lesson) return;
    await deleteResource({
      ...lessonWriteLocator,
      resourceId,
    }).unwrap();
  };

  const handleCreateFromTemplate = async (templateId: string) => {
    if (!lesson) return;
    return createFromTemplate({
      ...lessonWriteLocator,
      templateId,
    }).unwrap();
  };

  const handleGenerateFromTemplate = async (request: GenerateFromTemplateRequest) => {
    return generateFromTemplate(request).unwrap();
  };

  const handleDuplicateResource = async (resourceData: Resource) => {
    if (!lesson || resourceData.resourceKind === 'file') return;
    const clonedContent = JSON.parse(JSON.stringify(resourceData.content ?? {}));
    const duplicateContent =
      resourceData.resourceKind === 'document'
        ? stripStorageImagePathsFromDocumentContent(clonedContent)
        : resourceData.resourceKind === 'slide'
          ? stripStorageImagePathsFromSlideContent(clonedContent)
          : clonedContent;

    return createResource({
      ...lessonWriteLocator,
      resource: {
        resourceKind: resourceData.resourceKind,
        title: `Copy of ${resourceData.title}`,
        description: resourceData.description,
        content: duplicateContent,
      },
    }).unwrap();
  };

  return (
    <Box sx={{ py: 4, px: { xs: 2, md: 4 } }}>
      {exportError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {exportError}
        </Alert>
      )}
      {/* Header Section */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          columnGap: 2,
          rowGap: 1,
          alignItems: 'start',
          mb: 3,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <EditableText
            value={lesson?.title ?? ''}
            onSave={handleUpdateTitle}
            canEdit={canEdit}
            isLoading={isUpdatingLesson}
            typographyProps={{
              variant: 'h2',
              component: 'h1',
            }}
            textFieldProps={{
              sx: {
                '& .MuiInputBase-input': {
                  fontSize: '2.125rem',
                  fontWeight: 300,
                  lineHeight: 1.235,
                },
              },
            }}
          />
        </Box>
        {((!isMobile && user) || canEdit) && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 1,
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
            }}
          >
            {!isMobile && user && pdfExportEnabled && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<PictureAsPdfOutlinedIcon />}
                onClick={handleExportPdf}
                disabled={isExportingPdf}
              >
                {isExportingPdf ? 'Exporting...' : 'Export PDF'}
              </Button>
            )}
            {canEdit &&
              (isMobile ? (
                <>
                  <IconButton size="small" onClick={() => setShareOpen(true)} title="Share">
                    <ShareIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => dispatch(toggleScopedAiPanel())}
                    title="AI CoPlanner"
                  >
                    <AutoAwesomeIcon />
                  </IconButton>
                </>
              ) : (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ShareIcon />}
                    onClick={() => setShareOpen(true)}
                  >
                    Share
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<AutoAwesomeIcon />}
                    onClick={() => dispatch(toggleScopedAiPanel())}
                  >
                    AI CoPlanner
                  </Button>
                  {scoperEnabled && scoperContext && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      onClick={() => setScoperOpen(true)}
                      sx={{ borderStyle: 'dashed' }}
                    >
                      Dev AI
                    </Button>
                  )}
                </>
              ))}
          </Box>
        )}
        <Box sx={{ gridColumn: '1 / -1', minWidth: 0 }}>
          <EditableText
            value={lesson?.description ?? ''}
            onSave={handleUpdateDescription}
            canEdit={canEdit}
            isLoading={isUpdatingLesson}
            allowEmpty
            multiline
            typographyProps={{
              variant: 'subtitle1',
              color: 'text.secondary',
            }}
          />
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Overview Section */}
      <Box sx={{ mb: 4 }}>
        <LessonOverviewCard lesson={lesson} canEdit={canEdit} parentScope={parentScope} />
      </Box>

      {/* Resources Section */}
      <Box sx={{ mb: 4 }}>
        <ResourceListCard
          locator={lessonListLocator}
          disableGenerate={!courseIdForScope}
          resources={resources}
          templates={templates}
          onCreateResource={handleCreateResource}
          onCreateFromTemplate={handleCreateFromTemplate}
          onGenerateFromTemplate={handleGenerateFromTemplate}
          onUpdateResource={handleUpdateResource}
          onDelete={handleDeleteResource}
          onDuplicateResource={handleDuplicateResource}
          onExportToGoogle={exportToGoogle}
          canEdit={canManageResources}
          title="Lesson Resources"
        />
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Lesson Content Section */}
      <Box sx={{ mb: 4 }}>
        <Card>
          <CardHeader
            title="Lesson Content"
            sx={{
              py: 1,
              '&& .MuiCardHeader-title': {
                fontSize: '1.5rem',
                lineHeight: 1.3,
              },
            }}
          />
          <CardContent sx={{ pt: 0 }}>
            <LessonContent content={lesson.content} lessonId={lesson.id} canEdit={canEdit} />
          </CardContent>
        </Card>
      </Box>
      {lesson && (
        <ShareDialog
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          objectId={lesson.id}
          objectType="lesson"
          ownerId={lesson.owner_id}
          isPublic={lesson.is_public}
          onTogglePublic={(val) =>
            setPublicCascade({
              object_type: 'lesson',
              object_id: lesson.id,
              is_public: val,
            }).unwrap()
          }
          shareUrl={
            courseIdForScope && unitIdForScope
              ? `${window.location.origin}/curriculum/courses/${courseIdForScope}/units/${unitIdForScope}/lessons/${lesson.id}`
              : undefined
          }
        />
      )}
      {scoperEnabled && scoperContext && (
        <ScoperAiDialog
          open={scoperOpen}
          onClose={() => setScoperOpen(false)}
          context={scoperContext}
        />
      )}
    </Box>
  );
};

export default LessonPage;
